import { ChevronDown } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import type { StatusTone } from '~/components/ui/status-chip/tones';

interface StatusSelectProps {
  value: string;
  options: { value: string; label: string }[];
  /** Fires on pick; the caller saves immediately (inline list status, DESIGN.md §4.3). */
  onChange: (value: string) => void;
  /** The tone of a value, from the feature's status map (e.g. `aiStatus(v).tone`). */
  toneOf?: (value: string) => StatusTone;
  disabled?: boolean;
  /** Accessible name, e.g. `Status of Welcome message`. */
  ariaLabel: string;
  testId?: string;
}

/** `StatusChip`'s tinted surface plus a border, so the editable badge reads as a control. */
const SURFACE: Record<StatusTone, string> = {
  success:
    'border-success-500/40 bg-success-50 text-success-700 dark:bg-success-500/15 dark:text-success-500',
  warning:
    'border-warning-500/40 bg-warning-50 text-warning-700 dark:bg-warning-500/15 dark:text-warning-400',
  error: 'border-error-500/40 bg-error-50 text-error-700 dark:bg-error-500/15 dark:text-error-500',
  info: 'border-blue-light-500/40 bg-blue-light-50 text-blue-light-700 dark:bg-blue-light-500/15 dark:text-blue-light-500',
  neutral:
    'border-gray-300 bg-gray-100 text-gray-700 dark:border-gray-700 dark:bg-white/5 dark:text-white/80',
  brand: 'border-brand-500/40 bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-400',
  draft:
    'border-theme-purple-500/40 bg-theme-purple-500/10 text-theme-purple-500 dark:bg-theme-purple-500/15',
};

/**
 * A list row's status as a tinted badge that is also a picker: the native select sits invisibly
 * over the badge, so keyboard, screen readers and the option list are the browser's own.
 */
export function StatusSelect({
  value,
  options,
  onChange,
  toneOf,
  disabled,
  ariaLabel,
  testId,
}: StatusSelectProps) {
  const tone = toneOf?.(value) ?? 'neutral';
  const label = options.find((option) => option.value === value)?.label ?? 'Unknown';

  // A row-link cell navigates on click; stopping it here keeps the pick from opening the record.
  return (
    <span
      onClick={(event) => event.stopPropagation()}
      className={twMerge(
        'relative inline-flex h-7 w-28 items-center justify-between gap-1 rounded-lg border px-2.5 text-xs font-medium focus-within:ring-2 focus-within:ring-brand-500',
        SURFACE[tone],
        disabled && 'opacity-60',
      )}
    >
      <span className="truncate">{label}</span>
      <ChevronDown className="size-3.5 shrink-0" aria-hidden="true" />
      <select
        value={value}
        disabled={disabled}
        aria-label={ariaLabel}
        data-testid={testId}
        onChange={(event) => onChange(event.target.value)}
        className="absolute inset-0 cursor-pointer appearance-none opacity-0 disabled:cursor-not-allowed"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </span>
  );
}
